import ol = require("ol");
import _ = require("underscore");
import Portal = require("portal-gis");
import Utils = require("../utils");
import Base = require("./base/geoquery");
import KeywordValidator = require("./base/keywords");
import globals = require("../../globals");

function doif<T>(v: T, cb: (v: T) => void) {
    // findOption implementation is flawed...should return undefined, not false
    if (typeof v !== "undefined" && v !== <any>false) cb(v);
}

class Command extends Base<{
    out: {
        location: string
    }
}> {

    static about = `
google geocoder

Usage Example:
    
    <EVENT about="reverse-geocode on every map-click" event="map-click" id="google-reverse-locator" mid="app/commands/google-geocoder">
    <OPTIONS>
        <Values about="one of true,false,auto but in this case true will try to find an address when the map is clicked" id="reverse" value="true" />
        <Values about="valid values include street_address, neighborhood, political, postal_code, administrative_area_level_1, administrative_area_level_2" id="google-result-type" value="street_address" />
        <Values about="the place_id is a google feature identifier, use it as our feature key" id="key-template" value="&lt;%=place_id %&gt;" />
        <Values about="Place the marker on the map (the marker has a label so don't show the info viewer)" id="event" value="geoquery-result" />
        <Values about="Only executes when the shift key is pressed" id="condition" value="shift" />
    </OPTIONS>
    </EVENT>    
    
    `;

    static input = {
        "coordinates": {
            "about": "the click location",
        },
        "location": {
            "about": "the search text",
        },
        "map": {
            "about": "internal reference to the underlying map", // this should not be here
        },
    };

    static output = {
        "layers": {
            "about": "the search results",
        },
        "status": {
            "about": "status message from google, explains why layers.count = 0",
        },
    };

    static options = {
        "condition": {
            "about": "reverse geocoding only performed when alt|ctrl|shift|alt+shift pressed?",
            "domain": "alt|ctrl|shift|alt+shift",
        },
        "event": {
            "about": "the event to publish",
            "default": "geoquery-result",
        },
        "extent": {
            "about": "limits the search extent",
            "default": "_Map.Viewport.extent_",
        },
        "google-api": {
            "about": "google api endpoint",
            "default": globals["GoogleMapsUrl"],
        },
        "google-api-key": {
            "about": "google api key",
            "default": globals["GoogleMapsKey"],
        },
        "google-result-type": {
            "about": "comma-separated list of possible result types (e.g. street-address)",
            "domain": "street_address|neighborhood|political|postal_code|administrative_area_level_1|administrative_area_level_2",
            "default": "street_address",
        },
        "key-template": {
            "default": "<%= place_id %>",
        },
        "reverse": {
            "about": "auto|true|false to indicate if reverse-geocoding is desired",
            "default": "auto",
        },
    };

    get googleApi() {
        return this.options["google-api"];
    }

    get googleApiKey() {
        return this.options["google-api-key"];
    }

    private get api() {
        let d = $.Deferred<typeof google>();
        let params = {
            url: this.googleApi,
            key: this.googleApiKey
        }
        let mid = `../google-api!${JSON.stringify(params)}`;
        require([mid], () => d.resolve(google));
        return d;
    }

    // runs a google map locator query (for an address)
    execute(args = this.args) {
        if (!KeywordValidator.validate(this)) return;

        let options = Utils.defaults(this.asOptions(Command.options), {
            "key-template": "<%= place_id %>"
        });

        // to be generalized?        
        if (options.condition) {
            let event = <ol.MapBrowserEvent>args.out;
            switch (options.condition) {
                case "alt":
                    if (!ol.events.condition.altKeyOnly(event)) return;
                    break;
                case "ctrl":
                    if (!ol.events.condition.platformModifierKeyOnly(event)) return;
                    break;
                case "shift":
                    if (!ol.events.condition.shiftKeyOnly(event)) return;
                    break;
                case "alt+shift":
                    if (!ol.events.condition.altShiftKeysOnly(event)) return;
                    break;
                default:
                    // no built-in detection for ctrl alternatives
                    console.error("invalid condition");
                    return;
            }
        }

        let reverse = options.reverse;

        if (reverse === "auto" || reverse === "true") {
            doif(this.findOption("coordinate"), coordinate =>
                doif(this.findOption("map"), map =>
                    this.reverseGeocode({
                        coordinate: coordinate,
                        map: map,
                        googleResultType: options["google-result-type"]
                    })
                ));
        }
        if (reverse === "auto" || reverse === "false") {
            doif(this.findOption("location"), v => this.geocode({
                location: v,
                extent: options.extent,
                googleResultType: options["google-result-type"]
            }));
        }

    }

    private reverseGeocode(args: {
        map: ol.Map;
        coordinate: ol.Coordinate;
        googleResultType: string;
    }) {
        let resultTypes = Utils.split(Command.options['google-result-type'].default);
        doif(args.googleResultType, v => resultTypes = Utils.split(v));

        let sourceProj = args.map.getView().getProjection();
        let location = ol.proj.transform(args.coordinate, sourceProj, 'EPSG:4326');

        let latlng = new google.maps.LatLng(location[1], location[0]);

        this.api.done(google => {
            let geocoder = new google.maps.Geocoder();

            geocoder.geocode({ 'location': latlng }, (results, status) => {
                if (status == google.maps.GeocoderStatus.OK) {
                    // only consider results of the specified type
                    results = results.filter(r => resultTypes.some(type => 0 <= r.types.indexOf(type)));
                    if (results.length) {
                        let layer = <Rhythm.GIS.FeatureQueryResultLayer>{
                            spatialReference: {
                                wkid: <any>"EPSG:4326"
                            }
                        };

                        layer.features = results.map(r => ({
                            attributes: <any>{
                                text: r.formatted_address,
                                place_id: r["place_id"],
                                type: "address"
                            },
                            geometryType: "point",
                            geometry: [r.geometry.location.lng(), r.geometry.location.lat()]
                        }));

                        this.assignKeys(layer);
                        this.trigger({ layers: [layer] });

                    } else {
                        console.log('No results found');
                    }
                } else {
                    console.warn('Geocoder failed due to: ' + status);
                }
            });
        });
    }

    private geocode(args: {
        location: string;
        extent: string;
        googleResultType: string;
    }) {

        this.api.done(google => {

            let resultTypes = ["street_address"];
            doif(args.googleResultType, v => resultTypes = Utils.split(v));

            let address = args.location;
            let extent = args.extent;
            if (!extent) {
                let mapOptions = Utils.asDictionary(this.mediator.config.Map.Options.Values);
                extent = mapOptions["extent"] || this.mediator.config.Map.Viewport.extent;
            }
            let bounds: google.maps.LatLngBounds;
            if (extent) {
                let [left, bottom, right, top] = Utils.split(extent).map(parseFloat);
                let sw = new google.maps.LatLng(bottom, left);
                let ne = new google.maps.LatLng(top, right);
                bounds = new google.maps.LatLngBounds(sw, ne);
            } else {
                bounds = undefined;
            }


            let geocoder = new google.maps.Geocoder();

            geocoder.geocode({
                address: address,
                bounds: bounds
            }, (results, status) => {

                switch (status) {
                    case google.maps.GeocoderStatus.OK:
                        break;
                    default:
                        console.warn('Geocode was not successful for the following reason: ' + status);
                        this.trigger({
                            status: status,
                            layers: []
                        });
                        return;
                }

                let layer = <Rhythm.GIS.FeatureQueryResultLayer>{
                    spatialReference: {
                        wkid: <any>"EPSG:4326"
                    }
                };

                // only consider results of the specified type
                results = results.filter(r => resultTypes.some(type => 0 <= r.types.indexOf(type)));
                // filter out results not within the maps initial extent            
                if (bounds) results = results.filter(a => bounds.contains(a.geometry.location));
                layer.features = results.map(r => ({
                    attributes: <any>{
                        text: r.formatted_address,
                        place_id: r["place_id"],
                        type: "address"
                    },
                    geometryType: "point",
                    geometry: [r.geometry.location.lng(), r.geometry.location.lat()]
                }));

                this.assignKeys(layer);

                this.trigger({
                    status: status,
                    layers: [layer]
                });

            });
        });

    }
}

export = Command; 